import styled from "styled-components";
import { useEffect, useState } from "react";
import { X, CalendarPlus, ArrowRight, AlertTriangle } from "lucide-react";

import { theme } from "./theme";
import { useT } from "./i18n";
import { apiFetch } from "./api";
import { formatDate } from "./dateFormat";

/* Add nights to the end of a stay.
 *
 * An extension is a new line continuing this one, never an edit of it: the nights already paid keep
 * the quote they were sold at, and the added nights are priced at today's rate. The preview is not
 * decoration — the added nights push room inventory to eZee, and the price is resolved per calendar
 * date, so the FO has to see the figure before committing to it.
 *
 * See wiki/folio-ledger/extend-stay-and-the-door.md for what this does to the guest's card.
 */
const Backdrop = styled.div`
  align-items: center;
  background: rgba(15, 23, 42, 0.6);
  display: flex;
  inset: 0;
  justify-content: center;
  padding: 1rem;
  position: fixed;
  /* Above the drawer panel (1400). */
  z-index: 1500;
`;

const Card = styled.div`
  background: #ffffff;
  border-radius: 14px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.35);
  display: flex;
  flex-direction: column;
  max-height: 90vh;
  max-width: 460px;
  width: 100%;
`;

const Head = styled.div`
  align-items: flex-start;
  border-bottom: 1px solid ${theme.border};
  display: flex;
  gap: 0.6rem;
  padding: 0.85rem 1.1rem;
`;

const Title = styled.h3`
  align-items: center;
  color: ${theme.ink};
  display: flex;
  flex: 1 1 auto;
  font-size: 1rem;
  font-weight: 800;
  gap: 0.35rem;
  margin: 0;
`;

const Close = styled.button`
  background: transparent;
  border: 0;
  color: ${theme.ink700};
  cursor: pointer;
  padding: 0.1rem;
`;

const Body = styled.div`
  overflow-y: auto;
  padding: 0.8rem 1.1rem;
`;

const Current = styled.div`
  background: #faf9f6;
  border: 1px solid ${theme.border};
  border-radius: 9px;
  color: #4a4f63;
  font-size: 0.82rem;
  font-weight: 700;
  margin-bottom: 0.7rem;
  padding: 0.5rem 0.65rem;
`;

const Window = styled.div`
  align-items: center;
  color: ${theme.ink};
  display: flex;
  font-size: 0.9rem;
  font-weight: 800;
  gap: 0.35rem;
  margin-top: 0.15rem;
`;

const Label = styled.label`
  color: #4a4f63;
  display: block;
  font-size: 0.78rem;
  font-weight: 800;
  margin-bottom: 0.25rem;
`;

const Nights = styled.div`
  display: flex;
  gap: 0.35rem;
`;

const NightBtn = styled.button`
  background: ${(props) => (props.$on ? theme.ink : "#ffffff")};
  border: 1px solid ${(props) => (props.$on ? theme.ink : theme.border)};
  border-radius: 8px;
  color: ${(props) => (props.$on ? "#ffffff" : theme.ink)};
  cursor: pointer;
  flex: 1 1 0;
  font-size: 0.86rem;
  font-weight: 800;
  padding: 0.45rem 0;
`;

const Total = styled.div`
  align-items: center;
  background: #e6f6ee;
  border-radius: 9px;
  display: flex;
  justify-content: space-between;
  margin-top: 0.7rem;
  padding: 0.55rem 0.7rem;
  span {
    color: #2f5d47;
    font-size: 0.8rem;
    font-weight: 700;
  }
  strong {
    color: #17472f;
    font-size: 0.95rem;
    font-weight: 800;
  }
`;

const Warn = styled.div`
  align-items: flex-start;
  background: #fdf3e3;
  border-radius: 8px;
  color: #8a5a00;
  display: flex;
  font-size: 0.78rem;
  font-weight: 700;
  gap: 0.3rem;
  margin-top: 0.6rem;
  padding: 0.45rem 0.6rem;
`;

const Err = styled.div`
  background: #fdecea;
  border-radius: 8px;
  color: #b42318;
  font-size: 0.8rem;
  font-weight: 700;
  margin-top: 0.6rem;
  padding: 0.45rem 0.6rem;
`;

const Foot = styled.div`
  border-top: 1px solid ${theme.border};
  display: flex;
  gap: 0.45rem;
  justify-content: flex-end;
  padding: 0.7rem 1.1rem;
`;

const Btn = styled.button`
  align-items: center;
  border-radius: 8px;
  cursor: pointer;
  display: inline-flex;
  font-size: 0.86rem;
  font-weight: 800;
  gap: 0.3rem;
  padding: 0.5rem 0.9rem;
  &:disabled {
    cursor: not-allowed;
    opacity: 0.45;
  }
`;

const Ghost = styled(Btn)`
  background: #ffffff;
  border: 1px solid ${theme.border};
  color: ${theme.ink};
`;

const Primary = styled(Btn)`
  background: ${theme.ink};
  border: 0;
  color: #ffffff;
`;

const NIGHT_CHOICES = [1, 2, 3, 4, 5];

const ExtendStayModal = (props) => {
  const { t, lang } = useT();
  const line = props.line || {};
  const [nights, setNights] = useState(1);
  const [preview, setPreview] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const fmt = (value) => `Rp${Number(value || 0).toLocaleString("en-US")}`;
  /* Stored WITH the night count it was computed for, so "no preview yet" is a comparison rather than
   * a second piece of state cleared from inside the effect.
   */
  const plan = preview?.for === nights ? preview.data : null;

  useEffect(() => {
    let active = true;
    apiFetch(
      `/frontdesk_v2/bookings/${props.bookingId}/extend_stay_preview?extend_line_id=${line.id}&nights=${nights}&line_id=${line.id}`,
    )
      .then((res) => {
        if (!active) return;
        if (res?.data) {
          setPreview({ for: nights, data: res.data });
          setError("");
        } else {
          setPreview(null);
          setError(res?.message || t("error_body"));
        }
      })
      .catch(() => {
        if (active) {
          setPreview(null);
          setError(t("error_body"));
        }
      });
    return () => {
      active = false;
    };
  }, [nights, props.bookingId, line.id, t]);

  const submit = async () => {
    setBusy(true);
    setError("");
    try {
      const res = await apiFetch(
        `/frontdesk_v2/bookings/${props.bookingId}/extend_stay`,
        {
          method: "POST",
          body: JSON.stringify({
            extend_line_id: line.id,
            nights,
            line_id: line.id,
          }),
        },
      );
      if (res?.data?.booking) {
        props.onChanged(res.data, res.message);
      } else {
        setError(res?.message || t("error_body"));
      }
    } catch {
      setError(t("error_body"));
    }
    setBusy(false);
  };

  const blockers = plan?.errors || [];
  const canSubmit = !busy && plan?.can_extend && blockers.length === 0;

  return (
    <Backdrop onClick={props.onClose}>
      <Card onClick={(event) => event.stopPropagation()}>
        <Head>
          <Title>
            <CalendarPlus size={16} />
            {t("act_extend_stay")}
          </Title>
          <Close type="button" aria-label={t("close")} onClick={props.onClose}>
            <X size={18} />
          </Close>
        </Head>

        <Body>
          <Current>
            {t("ext_current")}
            <Window>
              {formatDate(line.check_in_date, lang)}
              <ArrowRight size={13} />
              {formatDate(line.check_out_date, lang)}
            </Window>
          </Current>

          <Label>{t("ext_nights")}</Label>
          <Nights>
            {NIGHT_CHOICES.map((choice) => (
              <NightBtn
                key={choice}
                type="button"
                $on={choice === nights}
                onClick={() => setNights(choice)}
              >
                {choice}
              </NightBtn>
            ))}
          </Nights>

          {plan ? (
            <Current style={{ marginTop: "0.7rem", marginBottom: 0 }}>
              {t("ext_becomes")}
              <Window>
                {formatDate(plan.from_date, lang)}
                <ArrowRight size={13} />
                {formatDate(plan.to_date, lang)}
              </Window>
            </Current>
          ) : null}

          {plan?.total_rupiah ? (
            <Total>
              <span>{t("ext_total")}</span>
              <strong>{fmt(plan.total_rupiah)}</strong>
            </Total>
          ) : null}

          {(plan?.warnings || []).map((warning) => (
            <Warn key={warning}>
              <AlertTriangle size={13} />
              {warning}
            </Warn>
          ))}

          {blockers.map((blocker) => (
            <Err key={blocker}>{blocker}</Err>
          ))}
          {error ? <Err>{error}</Err> : null}
        </Body>

        <Foot>
          <Ghost type="button" onClick={props.onClose} disabled={busy}>
            {t("confirm_cancel")}
          </Ghost>
          <Primary type="button" onClick={submit} disabled={!canSubmit}>
            {t("act_extend_stay")}
          </Primary>
        </Foot>
      </Card>
    </Backdrop>
  );
};

export default ExtendStayModal;
